import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import '../styles/training.css';
import '../styles/phrase-training.css';
import {
    initCamera,
    clearVideoMetadataCache,
    fetchGestureCounts,
    fetchUsers,
    uploadVideo,
    updateVideoRating,
    fetchUserSettings,
    saveUserSettings,
    fetchVideoUrl
} from '../utils/TrainingHelpers';
import VideoList from '../components/Training/VideoList';
import VideoPlayback from '../components/Training/VideoPlayback';
import SamplePhrasePopup from '../components/Training/PhraseTraining/SamplePhrasePopup';
import EmotionScoringModal from '../components/Training/PhraseTraining/EmotionScoringModal';
import NonProsodicModal from '../components/Training/PhraseTraining/NonProsodicModal';
import RecordingStatusOverlay from '../components/Training/PhraseTraining/RecordingStatusOverlay';
import SettingsPanel from '../components/Training/SettingsPanel';
import { phraseSets, baseVariations } from '../utils/PhraseSets';
import { jwtDecode } from 'jwt-decode';
import { FaQuestion } from 'react-icons/fa';
import { IoMdSettings } from 'react-icons/io';

const baseVariationKeys = baseVariations.map(v => v.key);
const DEFAULT_PHRASE_COUNTDOWN = 2;

const PhraseTraining = () => {
    const [selectedPhrase, setSelectedPhrase] = useState('');
    const [selectedVariation, setSelectedVariation] = useState('');
    const [, setVideos] = useState([]);
    const [error, setError] = useState('');
    const [showCamera, setShowCamera] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [stream, setStream] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showSamplePopup, setShowSamplePopup] = useState(false);
    const [showEmotionModal, setShowEmotionModal] = useState(false);
    const [showNonProsodicModal, setShowNonProsodicModal] = useState(false);
    const [showHelpHint, setShowHelpHint] = useState(false);
    const [popupVariation, setPopupVariation] = useState('');
    const [popupStep, setPopupStep] = useState('select');
    const [editingVideo, setEditingVideo] = useState(null);
    const [emotionVideoUrl, setEmotionVideoUrl] = useState('');
    const [gestureCounts, setGestureCounts] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState(new Set());
    const [tempFilters, setTempFilters] = useState(new Set());
    const [isAdmin, setIsAdmin] = useState(false);
    const [viewAllUsers, setViewAllUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [currentUserId, setCurrentUserId] = useState('');
    const videoRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timeoutRef = useRef(null);
    const pendingBlobRef = useRef(null);
    const videoListRef = useRef(null);
    const prevEmotionModalRef = useRef(showEmotionModal);
    const prevNonProsodicModalRef = useRef(showNonProsodicModal);
    const helpHintTimerRef = useRef(null);
    const [countdown, setCountdown] = useState(0);
    const countdownTimerRef = useRef(null);
    const [phraseCountdownLength, setPhraseCountdownLength] = useState(DEFAULT_PHRASE_COUNTDOWN);
    const [showSettings, setShowSettings] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('jwt');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setIsAdmin(decodedToken.role === 'admin');
                const userId = decodedToken.id || decodedToken.username;
                setCurrentUserId(userId);
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        }
    }, []);

    useEffect(() => {
        let url;
        if (showEmotionModal) {
            if (editingVideo) {
                fetchVideoUrl(editingVideo.id)
                    .then(setEmotionVideoUrl)
                    .catch(err => {
                        console.error('Error fetching video URL:', err);
                        setEmotionVideoUrl('');
                    });
            } else if (pendingBlobRef.current) {
                url = URL.createObjectURL(pendingBlobRef.current);
                setEmotionVideoUrl(url);
            }
        } else {
            setEmotionVideoUrl('');
        }
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [showEmotionModal, editingVideo]);

    useEffect(() => {
        const loadUsers = async () => {
            if (viewAllUsers && isAdmin) {
                try {
                    const list = await fetchUsers();
                    setUsers(list);
                } catch (err) {
                    console.error('Error fetching users:', err);
                }
            } else {
                setUsers([]);
                setSelectedUser('');
            }
        };
        loadUsers();
    }, [viewAllUsers, isAdmin]);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await fetchUserSettings();
                if (settings.phraseCountdown) {
                    setPhraseCountdownLength(settings.phraseCountdown);
                }
            } catch (err) {
                console.error('Error fetching user settings:', err);
            }
        };
        loadSettings();
    }, []);

    useEffect(() => {
        if (showCamera) {
            initCamera(videoRef, setStream, setError);
        }
    }, [showCamera]);

    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(t => t.stop());
            }
        };
    }, [stream]);

    useEffect(() => {
        clearVideoMetadataCache();
    }, []);

    useEffect(() => {
        const emotionClosed = prevEmotionModalRef.current && !showEmotionModal;
        const nonProsodicClosed = prevNonProsodicModalRef.current && !showNonProsodicModal;
        if ((emotionClosed || nonProsodicClosed) && showCamera) {
            initCamera(videoRef, setStream, setError);
        }
        prevEmotionModalRef.current = showEmotionModal;
        prevNonProsodicModalRef.current = showNonProsodicModal;
    }, [showEmotionModal, showNonProsodicModal, showCamera]);

    const startCountdown = useCallback((length) => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
        setCountdown(length);
        return new Promise(resolve => {
            countdownTimerRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                        resolve();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    const updateGestureCounts = useCallback((gestureName, increment, newCounts = null, newTotal = null) => {
        if (newCounts !== null && newTotal !== null) {
            setGestureCounts(newCounts);
            setTotalCount(newTotal);
        } else if (gestureName) {
            setGestureCounts(prev => ({ ...prev, [gestureName]: (prev[gestureName] || 0) + increment }));
            setTotalCount(prev => prev + increment);
        }
    }, []);

    useEffect(() => {
        const loadCounts = async () => {
            try {
                const data = await fetchGestureCounts(viewAllUsers, selectedUser || null, 'phrase');
                updateGestureCounts(null, 0, data.counts, data.total);
            } catch (err) {
                console.error('Error fetching gesture counts:', err);
            }
        };
        loadCounts();
    }, [viewAllUsers, selectedUser, updateGestureCounts]);

    const handlePhraseChange = (e) => {
        const value = e.target.value;
        setSelectedPhrase(value);
        if (value) {
            setPopupVariation('');
            setPopupStep('select');
            setShowSamplePopup(true);
        }
    };

    const recordedVariations = (phraseId) => {
        return Object.keys(gestureCounts)
            .filter(key => key.startsWith(`${phraseId}_`))
            .map(key => key.substring(phraseId.length + 1));
    };

    const handleStartFromPopup = (variation) => {
        setSelectedVariation(variation);
        setShowSamplePopup(false);
        startRecording(variation);
        setPopupVariation('');
        setPopupStep('select');
    };

    const startRecording = async (variation) => {
        if (!stream) return;
        setIsRecording(true);
        await startCountdown(phraseCountdownLength);
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8' });
        recorderRef.current = recorder;
        chunksRef.current = [];
        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                chunksRef.current.push(e.data);
            }
        };
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'video/webm' });
            pendingBlobRef.current = blob;
            setIsRecording(false);
            const recordedVariation = variation || selectedVariation;
            if (baseVariationKeys.includes(recordedVariation)) {
                setShowNonProsodicModal(true);
            } else {
                setShowEmotionModal(true);
            }
        };
        recorder.start();
        timeoutRef.current = setTimeout(() => stopRecording(), 60000);
    };

    const stopRecording = useCallback(() => {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop();
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if ((e.key === ' ' || e.key === 'Enter') && isRecording && countdown === 0) {
                e.preventDefault();
                stopRecording();
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [isRecording, countdown, stopRecording]);

    const handleEmotionSubmit = async ({ scores, sentiment, speed, non_manuals, notes }) => {
        setShowEmotionModal(false);
        const rating = { emotionScores: scores, sentiment, speed, non_manuals, notes };
        if (editingVideo) {
            try {
                await updateVideoRating(editingVideo.id, rating, setError);
                if (videoListRef.current && videoListRef.current.refreshBatch) {
                    videoListRef.current.refreshBatch(editingVideo.batchId);
                }
            } catch (err) {
                // Error handled via setError in helper
            }
            setEditingVideo(null);
            return;
        }

        const blob = pendingBlobRef.current;
        if (!blob) return;
        const gestureName = `${selectedPhrase}_${selectedVariation}`;
        setIsUploading(true);
        const meta = await uploadVideo(
            blob,
            gestureName,
            setError,
            setVideos,
            null,
            null,
            'phrase',
            rating
        );
        setIsUploading(false);
        if (meta) {
            setVideos(prev => [meta, ...prev]);
            const phrase = phraseSets.find(p => p.id === selectedPhrase);
            const variationsCount = recordedVariations(selectedPhrase).length + 1;
            const hasMoreVariations = phrase && variationsCount < phrase.variations.length;
            updateGestureCounts(gestureName, 1);
            if (videoListRef.current && videoListRef.current.refreshBatches) {
                videoListRef.current.refreshBatches();
            }
            if (hasMoreVariations) {
                setPopupVariation('');
                setPopupStep('select');
                setShowSamplePopup(true);
            } else {
                setSelectedPhrase('');
            }
        } else {
            setSelectedPhrase('');
        }
        pendingBlobRef.current = null;
        setSelectedVariation('');
    };

    const handleEmotionCancel = () => {
        setShowEmotionModal(false);
        pendingBlobRef.current = null;

        if (editingVideo) {
            setSelectedPhrase('');
            setSelectedVariation('');
            setEditingVideo(null);
            return;
        }

        setPopupVariation(selectedVariation);
        setPopupStep('samples');
        setShowSamplePopup(true);
    };

    const handleNonProsodicSubmit = ({ non_manuals, notes } = {}) => {
        setShowNonProsodicModal(false);
        const zeroScores = ['Happy', 'Sad', 'Angry', 'Fear', 'Disgust', 'Surprise']
            .reduce((acc, emotion) => ({ ...acc, [emotion]: 0 }), {});
        handleEmotionSubmit({
            scores: zeroScores,
            sentiment: 0,
            speed: selectedVariation,
            non_manuals,
            notes,
        });
    };

    const handleNonProsodicCancel = () => {
        setShowNonProsodicModal(false);
        handleEmotionCancel();
    };

    const handleSaveSettings = async (settings) => {
        try {
            await saveUserSettings(settings);
        } catch (err) {
            console.error('Error saving settings:', err);
        }
    };

    const handleEditRating = (video) => {
        const variation = video?.gesture?.split('_')[1];
        if (baseVariationKeys.includes(variation)) return;
        setEditingVideo(video);
        setShowEmotionModal(true);
    };

    const showHintTemporarily = () => {
        setShowHelpHint(true);
        clearTimeout(helpHintTimerRef.current);
        helpHintTimerRef.current = setTimeout(() => setShowHelpHint(false), 3000);
    };

    const handleClosePopup = () => {
        setShowSamplePopup(false);
        showHintTemporarily();
    };

    const handleBackToCamera = () => {
        setShowCamera(true);
        setSelectedVideo(null);
    };

    const currentPhrase = phraseSets.find(p => p.id === selectedPhrase);
    const availablePhrases = phraseSets.filter(p =>
        recordedVariations(p.id).length < p.variations.length
    );
    const emotionPhrase = editingVideo
        ? (phraseSets.find(p => p.id === editingVideo.gesture.split('_')[0])?.text || '')
        : currentPhrase ? currentPhrase.text : '';
    const emotionVariation = editingVideo
        ? editingVideo.gesture.split('_')[1]
        : selectedVariation;

    const initialScores = useMemo(
        () => editingVideo?.rating?.emotionScores,
        [editingVideo]
    );
    const initialSentiment = useMemo(
        () => editingVideo?.rating?.sentiment,
        [editingVideo]
    );
    const initialSpeed = useMemo(
        () => editingVideo?.rating?.speed,
        [editingVideo]
    );
    const initialNonManualMarkers = useMemo(() => (
        editingVideo?.rating?.non_manuals
            ? Object.fromEntries(
                Object.entries(editingVideo.rating.non_manuals).map(([key, value]) => [
                    key
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' '),
                    value,
                ])
            )
            : undefined
    ), [editingVideo]);
    const initialNotes = useMemo(
        () => editingVideo?.rating?.notes,
        [editingVideo]
    );

    return (
        <div className="training-container">
            {!showEmotionModal && !showNonProsodicModal && (
                <>
                    <div className="video-section">
                        {showCamera ? (
                            <>
                                <div className={`training-video-container${isRecording && countdown === 0 ? ' recording-active' : ''}`}>
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="video-preview"
                                    />
                                    <RecordingStatusOverlay
                                        isRecording={isRecording}
                                        phrase={currentPhrase ? currentPhrase.text : ''}
                                        signs={currentPhrase ? currentPhrase.signs : []}
                                        variation={selectedVariation}
                                    />
                                </div>
                                <div className="recording-info">
                                    {countdown > 0 && (
                                        <div className="countdown">{countdown}</div>
                                    )}
                                </div>
                                <div className="controls">
                                    {isRecording ? (
                                        countdown > 0 ? null : (
                                            <button onClick={stopRecording} className="record-button">
                                                Stop Recording (press space)
                                            </button>
                                        )
                                    ) : (
                                        <div className="phrase-controls">
                                            <button
                                                onClick={() => setShowSettings(true)}
                                                className="settings-button"
                                            >
                                                <IoMdSettings style={{ width: '24px', height: '24px' }} />
                                            </button>
                                            <select
                                                className="gesture-dropdown"
                                                value={selectedPhrase}
                                                onChange={handlePhraseChange}
                                                disabled={isUploading}
                                            >
                                                <option value="">Select a phrase</option>
                                                {availablePhrases.map(p => (
                                                    <option key={p.id} value={p.id}>{p.text}</option>
                                                ))}
                                            </select>
                                            {selectedPhrase && !showSamplePopup && (
                                                <div className="help-button-container">
                                                    <button
                                                        className="sample-help-button"
                                                        onClick={() => setShowSamplePopup(true)}
                                                    >
                                                        <FaQuestion style={{ width: '18px', height: '18px' }} />
                                                    </button>
                                                    {showHelpHint && <div className="help-hint" />}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <VideoPlayback
                                selectedVideo={selectedVideo}
                                handleBackToCamera={handleBackToCamera}
                            />
                        )}
                        {error && <p className="error-message">{error}</p>}
                    </div>
                    <VideoList
                        setError={setError}
                        selectedVideo={selectedVideo}
                        setSelectedVideo={setSelectedVideo}
                        setShowCamera={setShowCamera}
                        viewAllUsers={viewAllUsers}
                        setViewAllUsers={setViewAllUsers}
                        gestureCounts={gestureCounts}
                        totalCount={totalCount}
                        updateGestureCounts={updateGestureCounts}
                        isAdmin={isAdmin}
                        handleRecordingProcess={() => {}}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        showFilterPopup={showFilterPopup}
                        setShowFilterPopup={setShowFilterPopup}
                        tempFilters={tempFilters}
                        setTempFilters={setTempFilters}
                        gestures={[{ id: 'phrases', label: 'Phrases', gestures: availablePhrases.map(p => p.id) }]}
                        isBatchActive={false}
                        lockedBatchId={null}
                        isAdrian={false}
                        onAddReplacements={() => {}}
                        onEditRating={handleEditRating}
                        users={users}
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser}
                        currentUserId={currentUserId}
                        ref={videoListRef}
                        page="phrase"
                    />
                </>
            )}
            {currentPhrase && (
                <SamplePhrasePopup
                    visible={showSamplePopup}
                    phrase={currentPhrase.text}
                    phraseId={currentPhrase.id}
                    videos={[]}
                    variations={currentPhrase.variations.map(v => v.key)}
                    recordedVariations={recordedVariations(currentPhrase.id)}
                    signs={currentPhrase.signs}
                    initialVariation={popupVariation}
                    initialStep={popupStep}
                    onVariationChange={setPopupVariation}
                    onStepChange={setPopupStep}
                    onClose={handleClosePopup}
                    onStart={handleStartFromPopup}
                />
            )}
            <EmotionScoringModal
                visible={showEmotionModal}
                onSubmit={handleEmotionSubmit}
                onCancel={handleEmotionCancel}
                initialScores={initialScores}
                initialSentiment={initialSentiment}
                initialSpeed={initialSpeed}
                initialNonManualMarkers={initialNonManualMarkers}
                initialNotes={initialNotes}
                isEditing={Boolean(editingVideo)}
                phrase={emotionPhrase}
                variation={emotionVariation}
                videoUrl={emotionVideoUrl}
                editingVideoId={editingVideo?.id}
            />
            <NonProsodicModal
                visible={showNonProsodicModal}
                onSubmit={handleNonProsodicSubmit}
                onCancel={handleNonProsodicCancel}
            />
            <SettingsPanel
                visible={showSettings}
                singleCountdown={true}
                countdown={phraseCountdownLength}
                setCountdown={setPhraseCountdownLength}
                onClose={() => setShowSettings(false)}
                onSave={handleSaveSettings}
                countdownKey="phraseCountdown"
            />
        </div>
    );
};

export default PhraseTraining;

